// Entry validation — pure rules used by the web app (pre-submit UX) and enforced
// authoritatively by the Cloud Function that writes entries.
//
// Football is a Build-Your-Card bet-builder. An entry is a set of CARDS (one per
// fixture the player chose to enter); each card is a 1X2 `outcome` anchor plus
// 0..CONFIG.COMBO.MAX_LEGS multiplier legs drawn from that fixture's eligible
// `legCatalog`. There is no confidence ranking and no fixed pick count — you back as
// many or as few fixtures as you like.
import { CONFIG } from './config.js';
import { ANCHOR_MARKET_ID, isLegAllowed, isValidSelectionValue, MARKETS } from './markets.js';
const ok = { ok: true };
const fail = (error) => ({ ok: false, error });
const EXACT_SCORE_RE = /^\d{1,2}-\d{1,2}$/;
// A scorer leg stores a provider player id. Validation is lenient but bounded (an id
// token, not free text): a wrong/garbage id can never falsely hit because grading is
// `result.scorerIds.includes(id)` for THAT fixture, so an unreal id just misses. This
// only stops oversized/abusive values from being persisted.
const SCORER_ID_RE = /^[A-Za-z0-9_-]{1,32}$/;
/** Is a string a well-formed "H-A" exact score? */
export function isExactScore(value) {
    return typeof value === 'string' && EXACT_SCORE_RE.test(value);
}
/** Is a value a well-formed scorer player-id token? */
export function isScorerId(value) {
    return typeof value === 'string' && SCORER_ID_RE.test(value);
}
/**
 * Validate a single card: a valid 1X2 anchor plus up to MAX_LEGS distinct legs, with
 * a well-formed selection (never the anchor). Leg eligibility follows `isLegAllowed`:
 * always-on (score/half-time) legs are valid on every fixture, while coverage-gated
 * legs (events/stats) must be in the fixture's frozen `legCatalog`. `legCatalog` is
 * that frozen list; when omitted (caller doesn't know) the catalog membership check
 * is skipped and only selection shape + the always-on/coverage rule are enforced.
 */
export function validateCard(p, legCatalog) {
    if (!p.fixtureId)
        return fail('Card is missing a fixtureId.');
    const anchor = MARKETS[ANCHOR_MARKET_ID];
    if (!isValidSelectionValue(anchor, p.outcome))
        return fail(`Invalid result pick for ${p.fixtureId}.`);
    const legs = p.legs ?? [];
    if (legs.length > CONFIG.COMBO.MAX_LEGS) {
        return fail(`Too many legs for ${p.fixtureId} (max ${CONFIG.COMBO.MAX_LEGS}).`);
    }
    const seen = new Set();
    for (const leg of legs) {
        const m = MARKETS[leg.marketId];
        if (!m)
            return fail(`Unknown leg "${leg.marketId}" for ${p.fixtureId}.`);
        if (m.anchor)
            return fail(`The result can't be added as a leg for ${p.fixtureId}.`);
        // Only enforce catalog membership when the caller knows the catalog; always-on
        // legs pass regardless, coverage legs must be members.
        if (legCatalog && !isLegAllowed(leg.marketId, legCatalog)) {
            return fail(`Leg "${leg.marketId}" isn't offered for ${p.fixtureId}.`);
        }
        if (seen.has(leg.marketId))
            return fail(`Duplicate leg "${leg.marketId}" for ${p.fixtureId}.`);
        seen.add(leg.marketId);
        if (!isValidSelectionValue(m, leg.selection))
            return fail(`Invalid ${m.shortLabel} pick for ${p.fixtureId}.`);
    }
    return ok;
}
/**
 * Validate a full football entry: between 1 and the slate size cards, no duplicate
 * fixture, every chosen fixture is one of the round's fixtures (when provided), and
 * every card well-formed against its fixture's leg catalog.
 */
export function validateFootballEntry(predictions, allowedFixtureIds, legCatalogFor) {
    if (!Array.isArray(predictions))
        return fail('Predictions must be an array.');
    const n = predictions.length;
    if (n < 1)
        return fail('Build at least one card.');
    const allowed = allowedFixtureIds ? new Set(allowedFixtureIds) : null;
    if (allowed && n > allowed.size)
        return fail(`Enter at most ${allowed.size} fixtures (got ${n}).`);
    const seen = new Set();
    for (const p of predictions) {
        const c = validateCard(p, legCatalogFor?.(p.fixtureId));
        if (!c.ok)
            return c;
        if (seen.has(p.fixtureId))
            return fail(`Duplicate card for fixture ${p.fixtureId}.`);
        seen.add(p.fixtureId);
        if (allowed && !allowed.has(p.fixtureId))
            return fail(`Fixture ${p.fixtureId} is not in this round.`);
    }
    return ok;
}
/**
 * Validate an entry against a round. Football is always the free Build-Your-Card
 * model (1..slate cards, per-fixture lock); the cycling duel path validates
 * separately via `cyclingValidation`. Used by both the web app (pre-submit UX) and
 * the authoritative Cloud Function.
 */
export function validateEntryForRound(predictions, round, legCatalogFor) {
    return validateFootballEntry(predictions, round.fixtureIds, legCatalogFor);
}
/** Legs in a canonical order (by market id), for byte-identity comparison. */
function normLegs(legs) {
    return [...(legs ?? [])].sort((a, b) => a.marketId.localeCompare(b.marketId));
}
/**
 * Do two cards carry an identical anchor AND identical legs (markets + selections)?
 * Order-insensitive on legs. `a` is optional so callers can compare against a
 * possibly-missing existing card. This is the byte-identity test the entry lock uses
 * to tell an unchanged re-submit from a real edit.
 */
export function samePrediction(a, b) {
    if (!a)
        return false;
    if (a.outcome !== b.outcome)
        return false;
    const la = normLegs(a.legs);
    const lb = normLegs(b.legs);
    if (la.length !== lb.length)
        return false;
    for (let i = 0; i < la.length; i++) {
        if (la[i].marketId !== lb[i].marketId || la[i].selection !== lb[i].selection)
            return false;
    }
    return true;
}
/**
 * The per-fixture freeze rule — the engine half of the entry lock the `submitEntry`
 * server enforces. Each card freezes at its OWN fixture's kickoff: a card whose
 * fixture has started must be present AND byte-identical (anchor + legs) in the
 * incoming submission — never added, changed, or dropped — while cards on still-open
 * fixtures are unrestricted. `hasStarted` answers, per fixture id, whether it has
 * kicked off. Returns the first violation found, or null when every frozen card is
 * respected. Pure, so client (pre-submit UX) and server share one rule.
 */
export function flexibleFreezeViolation(existing, incoming, hasStarted) {
    const existingByFixture = new Map();
    for (const p of existing)
        existingByFixture.set(p.fixtureId, p);
    const incomingByFixture = new Map();
    for (const p of incoming)
        incomingByFixture.set(p.fixtureId, p);
    const touched = new Set([...existingByFixture.keys(), ...incomingByFixture.keys()]);
    for (const fixtureId of touched) {
        if (!hasStarted(fixtureId))
            continue; // open fixture: free to add / edit / drop
        const prev = existingByFixture.get(fixtureId);
        const inc = incomingByFixture.get(fixtureId);
        if (!prev)
            return { kind: 'STARTED_NEW', fixtureId };
        if (!inc || !samePrediction(inc, prev))
            return { kind: 'CHANGED_FROZEN', fixtureId };
    }
    return null;
}
//# sourceMappingURL=validation.js.map